import React from 'react';
import { Tooltip } from 'react-tooltip';
import styled from 'styled-components';

import CloseIcon from '../svgs/CloseIcon';
import { useBodyScrollLock } from './common/useBodyScrollLock';

type Props = {
  isOpen: boolean;
  onClose?: () => void;
  children: React.ReactNode;
  customWidth?: string;
  customHeight?: string;
  customMinWidth?: string;
  customMinHeight?: string;
  paddingHorizontal?: string;
  paddingVertical?: string;
  alwaysShowCancel?: boolean;
  backgroundColor?: string;
  hasParentTransparency?: boolean;
  allowPropagation?: boolean;
  /** Blocking hint: shows a tooltip AND disables the close button. */
  closeTooltip?: string;
  /**
   * Non-blocking hint: same tooltip, close button stays live. For reassuring
   * about a side effect rather than preventing the close — e.g. "Close. This
   * won't cancel publishing" while a publish is in flight (PLAT-10947).
   * Ignored when `closeTooltip` is set, since that one wins and disables.
   */
  closeHint?: string;
};
const Modal = (props: Props) => {
  const {
    isOpen,
    onClose,
    children,
    customWidth = '500px',
    customHeight,
    customMinHeight,
    customMinWidth,
    paddingHorizontal = '20px',
    paddingVertical = '20px',
    alwaysShowCancel = false,
    backgroundColor = 'var(--background)',
    hasParentTransparency = true,
    allowPropagation,
    closeTooltip,
    closeHint,
  } = props;

  // `closeTooltip` wins: it's the blocking case, and disabling the button while
  // also reassuring the user it's safe to click would contradict itself.
  const tooltipContent = closeTooltip ?? closeHint;

  // Prevent body scroll when modal is open. Not gated on `allowPropagation`,
  // which is about React event bubbling and says nothing about native scroll:
  // the one dialog that sets it is the clip preview, the biggest on the app and
  // the one whose inner lists most need the wheel to stop at their ends.
  useBodyScrollLock(isOpen);

  if (!isOpen) return null;

  return (
    <Main
      onMouseUp={e => {
        if (allowPropagation) return;
        e.stopPropagation();
      }}
      onClick={e => {
        if (allowPropagation) return;
        e.stopPropagation();
      }}
      onKeyDown={e => {
        if (allowPropagation) return;
        e.stopPropagation();
      }}
      onKeyDownCapture={e => {
        if (allowPropagation) return;
        e.stopPropagation();
        e.nativeEvent.stopImmediatePropagation();
      }}
      onWheel={e => {
        if (allowPropagation) return;
        e.stopPropagation();
      }}
      hasParentTransparency={hasParentTransparency}
    >
      <Content
        width={customWidth}
        minWidth={customMinWidth}
        height={customHeight}
        minHeight={customMinHeight}
        paddingHorizontal={paddingHorizontal}
        paddingVertical={paddingVertical}
        backgroundColor={backgroundColor}
        id="modal"
      >
        {onClose && (
          <>
            <Close
              alwaysShowCancel={alwaysShowCancel}
              onClick={onClose}
              disabled={!!closeTooltip}
              aria-label={tooltipContent}
              data-tooltip-id="modal-close-tooltip"
              data-tooltip-content={tooltipContent}
            >
              <CloseIcon />
            </Close>
            {tooltipContent && (
              <Tooltip
                id="modal-close-tooltip"
                place="bottom"
                positionStrategy="fixed"
                style={{ zIndex: 100001 }}
              />
            )}
          </>
        )}

        {children}
      </Content>
    </Main>
  );
};

export default Modal;

const Main = styled.div<{ hasParentTransparency: boolean }>`
  width: 100%;
  height: 100%;
  background-color: ${props =>
    props.hasParentTransparency && 'rgba(0, 0, 0, 0.5)'};
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 1000005;
  backdrop-filter: var(--modal-blur);
  position: fixed;
  top: 0;
  left: 0;
`;

const Content = styled.div<{
  width: string;
  minWidth?: string;
  height?: string;
  minHeight?: string;
  paddingHorizontal: string;
  paddingVertical: string;
  backgroundColor: string;
}>`
  position: relative;
  background-color: ${props => props.backgroundColor};
  padding: ${props => props.paddingVertical} ${props => props.paddingHorizontal};
  border-radius: 10px;
  border: 1px solid var(--border);
  box-shadow: var(--elevation-toast);
  text-align: center;
  z-index: 1000;
  width: ${props => props.width};
  ${props => props.minWidth && `min-width: ${props.minWidth};`}
  ${props => props.height && `height: ${props.height};`}
  ${props => props.minHeight && `min-height: ${props.minHeight};`}
`;

const Close = styled.button<{ alwaysShowCancel: boolean; disabled?: boolean }>`
  position: absolute;
  top: 8px;
  right: 8px;
  cursor: ${props => (props.disabled ? 'not-allowed' : 'pointer')};
  background-color: transparent;
  border: 0;
  outline: 0;
  width: fit-content;
  padding: var(--spacing-sm);
  z-index: ${props => (props.alwaysShowCancel && '12') || '1'};
  color: var(--text-muted);
  border-radius: 6px;
  transition: all 0.15s ease;
  display: flex;
  align-items: center;
  justify-content: center;

  &:hover {
    color: var(--foreground);
    background-color: var(--surface-elevated);
  }
`;
